import {find, get, includes, keyBy, last, map, sample, some, toArray} from 'lodash';
import {
	changeStateAfterRound,
	changeStateInGame,
	changeStateOfGame,
	checkGameState,
	clearHereNowReducer,
	establishConnection,
	fetchContestsSuccess,
	getWagerSuccess,
	globalError,
	gotAnswerEvent,
	IDebugContest,
	makeAnswer,
	questionsAnswered,
	questionTimeEnd,
	recordQuestion,
	recordQuestions,
	removeAnswerEvent,
	setQuestionResult,
	setQuestionsResults,
	showQuestion,
	showSetQuestions,
	startTrivia,
	subscribeToLiveScores,
	unsubscribeFromLiveScores,
	utilsEvent,
	voidEvent,
	warningFinished
} from 'modules/actions';

import {ITriviaContest, ITriviaRound} from 'modules/reducers/contests_trivia';
import {IPayloadSockets, IQuestionResultAction, ISetWagerAction, ITriviaQuestion} from 'modules/reducers/trivia';
import {
	clearLiveQuestionsSaga,
	fetchAndWaitForContests,
	setContestStatus,
	setRoundStatus,
	setStatusesContestRound
} from 'modules/sagas/contests_trivia';
import {
	badgePointsSaga,
	checkSetOfAnswers,
	checkSetOfAnswersSummary,
	checkUserAnswer,
	getQuestionSummary,
	makeFakePick,
	perfectRoundFlow,
	prepareTriviaGame,
	sendAnalyticsAfterRound,
	sendGaEvent,
	sendGaRoundStartEvent,
	wagerRoundFlow
} from 'modules/sagas/trivia/utils_sagas';
import {
	getActualContest,
	getActualContestRound,
	getCompletedContests,
	getCompletedDailyContests,
	getCompletedNightContests,
	getContestByRoundIDSelector,
	getLastCompletedContest,
	getLastCompletedQuestionOfRound,
	getLastGameRound,
	getRoundByIDSelector,
	getScheduledRound
} from 'modules/selectors/contests_trivia';
import {isUserCaptain} from 'modules/selectors/herds';
import {getRegionSelector} from 'modules/selectors/settings';
import {
	getContestPointsFromWagersSelector,
	getStateOfGame,
	getTriviaQuestionByIDSelector
} from 'modules/selectors/trivia';

import {IFlow, ISagaAction, ISetFlow, Optional, TSagaGenerator, TSendGaRoundStartEventSaga} from 'modules/types';
import {ApiError, TriviaApi} from 'modules/utils/Api';
import {IS_TRIVIA_APP} from 'modules/utils/constants';
import {
	EnumAfterRoundState,
	EnumAnswerStates,
	EnumInGameState,
	EnumTriviaContestStatus,
	EnumTriviaRoundType,
	EnumTriviaStatesOfGame,
	EnumTriviaStatusType
} from 'modules/utils/enums';
import {AudioControl} from 'modules/utils/Sounds/AudioControl';
import {
	convertToCorrectAnswer,
	getTimeToComplete,
	prepareQuestionsToStore,
	setDebugTimes,
	showWarningScreen,
	teaserQuestion,
	TIMINGS
} from 'modules/utils/trivia';
import {SagaIterator} from 'redux-saga';
import {all, call, cancel, delay, fork, put, race, select, take} from 'typed-redux-saga';


/**
 * Recursive listener to get answers. Used in common trivia flow. Cancelable
 */
const listenToAnswers = function* (questions: ITriviaQuestion[]): SagaIterator {
	const action = yield* take(makeAnswer);
	const question_id = get(action, 'payload.question_id');
	const question = questions.find(({id}) => question_id === id);

	yield* call(checkUserAnswer, question);
	yield* call(listenToAnswers, questions);
};

export const roundResultsSaga = function* (round: ITriviaRound) {
	yield* fork(getUsersResult);
	yield* fork(perfectRoundFlow, round);
}

export const afterRoundActionsSaga = function* (round: ITriviaRound) {
	yield* put(changeStateOfGame(EnumTriviaStatesOfGame.loading));
	yield* put(changeStateInGame(EnumInGameState.unknown));
	yield* put(changeStateAfterRound(EnumAfterRoundState.unknown));
	yield* call(setRoundStatus, round, EnumTriviaStatusType.complete);
	yield* call(wagerRoundFlow);
	yield* put(changeStateOfGame(EnumTriviaStatesOfGame.after_round));
};

export const afterContestActionsSaga = function* (contest: ITriviaContest, round: ITriviaRound) {
	yield* put(changeStateOfGame(EnumTriviaStatesOfGame.loading));
	yield* put(changeStateInGame(EnumInGameState.unknown));

	if (!contest.is_replayed) {
		yield* call(fetchAndWaitForContests);
	}

	yield* call(setRoundStatus, round, EnumTriviaStatusType.complete);
	yield* call(setContestStatus, contest, EnumTriviaContestStatus.complete);

	const is_first_contest = contest.linked_contest_id && contest.position && contest.position === 1;

	/**
	 * If position of contest is 1 then we are waiting for next contest with position with number 2.
	 * This state is called as contest break.
	 */
	if (is_first_contest) {
		yield* put(changeStateOfGame(EnumTriviaStatesOfGame.contest_break));
		yield* delay(TIMINGS.end_game_delay)
		yield* put(subscribeToLiveScores({
			section: 'trivia'
		}));
	} else {

		yield* delay(5000);
		/**
		 * If position of contest is 2 then we are pushing post game state to the game
		 * This state is called as post_game/end_game
		 */
		yield* put(changeStateOfGame(EnumTriviaStatesOfGame.end_game));
		// yield* delay(TIMINGS.end_game_delay);
		yield* fork(loungeScreenFlow);
		yield* put(clearHereNowReducer());

		yield* delay(TIMINGS.end_game_delay)
		yield* put(subscribeToLiveScores({
			section: 'trivia'
		}));
	}
};


const singleQuestionFlowLogic = function* (action: IFlow) {
	const {question, round, contest, is_last_round} = action;
	const is_last_question = round?.number_questions === question.position;

	if (!question.is_practice) {
		if (is_last_question) {
			yield* fork(sendAnalyticsAfterRound, round, contest);
			if (is_last_round) {
				yield* call(afterContestActionsSaga, contest, round);
			} else {
				yield* call(afterRoundActionsSaga, round);
			}
			yield* call(roundResultsSaga, round);
		} else {
			yield* put(changeStateOfGame(EnumTriviaStatesOfGame.loading));
		}
	} else {
		yield* all<any>([
			put(removeAnswerEvent(question.id)),
			put(changeStateOfGame(EnumTriviaStatesOfGame.before_game))
		]);
	}
}

/**
 * The function has a cyclomatic complexity of 8 which is higher than the threshold of 7
 * Reason: if I change smth here it can be hard to read and understand for further support
 */
// eslint-disable-next-line complexity
export const singleQuestionFlow = function* (action: IFlow): SagaIterator {
	try {
		const {question, round, contest} = action;
		const is_fan_fact = get(question, 'fun_fact');

		yield* put(recordQuestion(question));

		yield* all<any[]>([
			put(unsubscribeFromLiveScores()),
			call(setStatusesContestRound, {
				actioned_contest: {id: contest.id, status: EnumTriviaContestStatus.live},
				actioned_round: {id: round.id, status: EnumTriviaStatusType.live}
			}),
			put(changeStateOfGame(EnumTriviaStatesOfGame.in_game)),
			put(showQuestion(question))
		]);

		if (showWarningScreen(question)) {
			yield* put(changeStateInGame(EnumInGameState.warning));
			yield* take(warningFinished);
		}

		yield* put(changeStateInGame(EnumInGameState.question));

		const listenerUserActions = yield* fork(listenToAnswers, [question]);

		const [is_time_end] = yield* race<any>([
			take(questionTimeEnd),
			take(gotAnswerEvent)
		]);

		yield* cancel(listenerUserActions);

		if (is_time_end) {
			yield* call(makeFakePick, question);
			yield* take(gotAnswerEvent)
		}

		yield* delay(500);

		const {answer_state, points} = yield* call(getQuestionSummary, question, round);

		yield* put(setQuestionResult({
			question_id: question.id,
			position: question.position,
			answer_state,
			points,
			round_id: round.id
		}));

		yield* fork(sendGaEvent, {question, contest, round, answer_state});

		yield* call(badgePointsSaga, answer_state, question, round);

		yield* delay(question.time_to_results * 1000);

		if (is_fan_fact) {
			yield* put(changeStateInGame(EnumInGameState.fun_fact));
		}

		yield* delay(question.time_to_fan_fact * 1000);

		yield* call(singleQuestionFlowLogic, action)

	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(establishConnection());
		yield* put(globalError(error));
	}
};


export const setOfQuestionFlow = function* (action: ISetFlow): SagaIterator {
	try {
		const {questions, round, contest} = action;

		const questions_to_record = prepareQuestionsToStore(questions, round.id);

		yield* all<any>([
			put(unsubscribeFromLiveScores()),
			call(setStatusesContestRound, {
				actioned_contest: {id: contest.id, status: EnumTriviaContestStatus.live},
				actioned_round: {id: round.id, status: EnumTriviaStatusType.live}
			}),
			put(changeStateOfGame(EnumTriviaStatesOfGame.in_game)),
			put(recordQuestions(questions_to_record)),
			put(showQuestion(questions[0])),
			put(showSetQuestions(questions))
		]);

		if (showWarningScreen(questions[0])) {
			yield* put(changeStateInGame(EnumInGameState.warning));
			yield* take(warningFinished);
		}

		yield* put(changeStateInGame(EnumInGameState.set_questions));

		const listenerUserActions = yield* fork(listenToAnswers, questions);

		const [is_time_end, answered] = yield* race<any>([
			take(questionTimeEnd),
			take(questionsAnswered),
			take(gotAnswerEvent)
		]);

		yield* cancel(listenerUserActions);

		yield* put(changeStateInGame(EnumInGameState.set_questions_result));
		if (is_time_end || answered) {
			yield* take(gotAnswerEvent)
		}

		const is_cap = yield* select(isUserCaptain);
		yield* delay(500);

		if (!is_cap && contest.is_herd) {
			yield* delay(1000);
			yield* call(checkSetOfAnswersSummary, questions, round);
		} else {
			yield* call(checkSetOfAnswers, questions, round);
		}

		yield* delay(TIMINGS.swipe_it_results);
		yield* fork(sendAnalyticsAfterRound, round, contest);
		yield* call(afterRoundActionsSaga, round);
		yield* call(roundResultsSaga, round);
		yield* put(showSetQuestions([]))

	} catch (e: any) {
		const error = e as Error;
		yield* put(establishConnection());
		yield* put(globalError(error));
	}
};


const runGameLogic  = function* (contest: Optional<ITriviaContest>, question: ITriviaQuestion) {
	if (contest?.is_herd && !IS_TRIVIA_APP) {
		AudioControl.BG_FADE_IN();
	}
	if (question?.position === 1) {
		yield* put(clearHereNowReducer());
	}
}
/**
 * Runs the main flow of the game. Can be canceled via Pub Nub events channel
 */
// tslint:disable-next-line:cyclomatic-complexity
export const runGame = function* (
	{payload: {questions, round_id}}: ISagaAction<IPayloadSockets>
): SagaIterator {
	try {
		yield* call(prepareTriviaGame, round_id);
		const question = questions[0];
		const contest = yield* select(getActualContest);
		const roundSelector = yield* select(getRoundByIDSelector);
		const round = roundSelector(round_id);
		const is_last_round = contest?.number_rounds === round?.position

		yield* call(runGameLogic, contest, question)

		if (!round || !contest) {
			return;
		}
		yield* fork<TSendGaRoundStartEventSaga>(sendGaRoundStartEvent, {
			question, round, contest
		});

		if (round?.type === EnumTriviaRoundType.this_or_that) {
			yield* race({
				task: yield* call<TSagaGenerator<ISetFlow>>(setOfQuestionFlow, {
					questions,
					round,
					contest,
					is_last_round
				}),
				cancel: yield* take(utilsEvent),
				cancel2: yield* take(startTrivia),
				cancel3: yield* take(voidEvent)
			});
		} else {
			yield* race({
				task: yield* call<TSagaGenerator<IFlow>>(singleQuestionFlow, {
					question,
					round,
					contest,
					is_last_round
				}),
				cancel: yield* take(utilsEvent),
				cancel2: yield* take(startTrivia),
				cancel3: yield* take(voidEvent)
			});
		}
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))
	}
};

export const runPracticeFlowSaga = function* (): SagaIterator {
	try {
		yield* delay(14000);
		yield* fork(singleQuestionFlow, {
			question: {
				...teaserQuestion.randQuestion,
				start_date_time: new Date().getTime(),
				end_date_time: new Date().getTime() + 20000
			},
			round: teaserQuestion.randRound,
			contest: teaserQuestion.randContest,
			is_last_round: false
		} as any);
		yield* delay(20000);
		yield* put(gotAnswerEvent([{'id': 217, 'value': '1', 'question_id': 999999, 'percents': 100}]));
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))
	}
};

export const debugTriviaQuestionFlow = function* (action: ISagaAction<number>) {
	try {
		const questionID = action.payload;
		const questionObjectSelector = yield* select(getTriviaQuestionByIDSelector);
		const questionObject = questionObjectSelector(questionID);

		if (questionObject) {
			yield* fork<any>(singleQuestionFlow, questionObject);

			yield* delay(questionObject.time_to_answer);
			yield* put(gotAnswerEvent(questionObject.answer));
		} else {
			alert('No questions found')
		}
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield put(globalError(error))
	}
};

export const debugTriviaRoundFlow = function* (action: ISagaAction<number>) {
	try {
		const roundId = action.payload;
		const roundObjectSelector = yield* select(getRoundByIDSelector);
		const round = roundObjectSelector(roundId);
		const contestByRound = yield* select(getContestByRoundIDSelector);
		const contest = contestByRound(round!.id);

		if (round) {


			yield* fork(setOfQuestionFlow, {
				questions: round.questions,
				round,
				contest,
				is_last: false,
				is_last_round: false
			} as any);

			const last_question: ITriviaQuestion | undefined = last(round.questions);

			if (!last_question) {
				throw new Error('no questions');
			}
			const delay_time = getTimeToComplete(last_question?.end_date_time || '', true);

			yield* delay(delay_time * 1000);

			const answers = map(round.questions, 'answer_choices');

			yield* put(gotAnswerEvent(answers));
		} else {
			throw new Error('no questions');
		}
	} catch (e: any) {
		console.log(e);
		alert('No questions found')
	}
};


const runReplay = function* (newContest: ITriviaContest) {

	for (const round of newContest.rounds) {
		if (round.type !== EnumTriviaRoundType.this_or_that) {
			for (const question of round.questions) {
				const questionStart = get(question, 'start_date_time', '');
				const delta = +new Date(questionStart) - +new Date();
				yield* delay(delta - 10 * 1000);
				yield* call(setContestStatus, newContest, EnumTriviaContestStatus.live);

				yield* put(startTrivia({
					round_id: round.id,
					questions: [question]
				}));
				const questionEnd = get(question, 'end_date_time', '');
				const deltaEnd = +new Date(questionEnd) - +new Date();
				yield* delay(deltaEnd);
				const answer = question.answer_choices.map(convertToCorrectAnswer)
				yield* put(gotAnswerEvent(answer))
			}
		} else {
			const roundStart = get(round, 'start_date_time', '');
			const delta = +new Date(roundStart) - +new Date();
			yield* delay(delta - 10 * 1000);
			yield* call(setContestStatus, newContest, EnumTriviaContestStatus.live);

			yield* put(startTrivia({
				round_id: round.id,
				questions: round.questions
			}));

			const lastQuestion: Optional<ITriviaQuestion> = last(round.questions);
			const lastQuestionEndTime = get(lastQuestion, 'end_date_time', '');
			const delay_time = getTimeToComplete(lastQuestionEndTime, true);

			yield* delay(delay_time * 1000);

			const answers = map(round.questions, (question) => {
				return question.answer_choices.map(convertToCorrectAnswer)
			});

			yield* put(gotAnswerEvent(answers));
		}
	}
}

const getSelector = (type?: IDebugContest['type']) => {
	if (type === 'night') {
		return getCompletedNightContests
	}
	if (type === 'daily') {
		return getCompletedDailyContests
	}
	return getCompletedContests
}
export const debugTriviaContestFlow = function* (action: ISagaAction<IDebugContest>) {
	try {
		yield* put(unsubscribeFromLiveScores());
		const contests = yield* select(getSelector(action.payload.type));
		const region = yield* select(getRegionSelector);
		const contestId = action.payload.id ? action.payload.id : sample(map(contests, 'id')) || 0;
		const contest = find(contests, {id: contestId});

		if (!contests.length) {
			throw new Error(`No completed contests for ${action.payload.type} (${region.toUpperCase()})`)
		}

		if (!contest) {
			throw new Error(`
			Please make sure that contest is for ${action.payload.type} (${region.toUpperCase()}),
			allowed ids - ${map(contests, 'id').join(', ')}`)
		}

		const prevDiff = +new Date(contest.end_date_time) - +new Date(contest.start_date_time);
		const newContest = setDebugTimes(contest);
		const newDiff = +new Date(newContest.end_date_time) - +new Date(newContest.start_date_time);

		if (prevDiff !== newDiff) {
			throw new Error(`Timing are not equal with original contest`)
		}

		yield* put(fetchContestsSuccess([
			{
				...newContest,
				status: EnumTriviaContestStatus.scheduled,
				is_replayed: true
			}
		]))

		yield* put(checkGameState())
		yield* call(runReplay, newContest)

	} catch (e: any) {
		console.log(e);
		alert(e)
	}
};

// checkGameStateSaga logic block
const isRoundBreakState = (actualContest: Optional<ITriviaContest>, isScheduledRound: boolean): boolean => {
	return actualContest?.status === EnumTriviaContestStatus.live && isScheduledRound
};

const isBetweenQuestions = (lastCompletedQuestion: Optional<ITriviaQuestion>, round: Optional<ITriviaRound>): boolean => {
	const isRoundLive = round?.status === EnumTriviaStatusType.live;

	if (lastCompletedQuestion && round) {
		const scheduledQuestion = lastCompletedQuestion.position < round.number_questions;
		return isRoundLive && scheduledQuestion;
	}
	return false;
};

const postGameState = (lastCompletedContest: Optional<ITriviaContest>): boolean => {
	if (!lastCompletedContest) {
		return false;
	}
	const ended = new Date(lastCompletedContest.end_date_time).getTime();
	const now = new Date().getTime();
	return now > ended && now - ended < TIMINGS.end_game_delay;
};

const isLiveState = (liveQuestion: Optional<ITriviaQuestion>, gameState: EnumTriviaStatesOfGame): boolean => {
	if (liveQuestion && liveQuestion.end_date_time) {
		const diff = new Date(liveQuestion.end_date_time).getTime() - new Date().getTime();

		if ( diff < 1000 ) {
			return includes([gameState], EnumTriviaStatesOfGame.loading)
		}
	}
	return false
};

const checkGameStateLogic = function* (round: ITriviaRound, lastCompletedContest: Optional<ITriviaContest>) {
	const actualContest = yield* select(getActualContest);
	const liveQuestion = round?.questions.find(it => it.status === EnumTriviaStatusType.live);
	const lastCompletedQuestion = yield* select(getLastCompletedQuestionOfRound);
	const gameState = yield* select(getStateOfGame);
	const isScheduledRound = round?.status === EnumTriviaStatusType.scheduled;
	const isSwipeIt = round?.type === EnumTriviaRoundType.this_or_that;
	/**
	 * attempts to run live question in the middle of the game
	 */
	if ( isLiveState(liveQuestion, gameState) ) {
		yield* put(startTrivia({
			questions: isSwipeIt ? round.questions : [liveQuestion!],
			round_id: round.id,
		}));
		return;
	}
	/**
	 * attempts to get users to the game in the middle of question
	 */
	if ( isBetweenQuestions(lastCompletedQuestion, round) ) {
		yield* put(changeStateOfGame(EnumTriviaStatesOfGame.in_game));
		yield* put(changeStateInGame(EnumInGameState.unknown));
		return;
	}

	/**
	 * round break screens
	 */
	if ( isRoundBreakState(actualContest, isScheduledRound) ) {
		yield* call(wagerRoundFlow);
		yield* put(changeStateOfGame(EnumTriviaStatesOfGame.after_round));
		return;
	}

	if (postGameState(lastCompletedContest)) {
		yield* put(changeStateOfGame(EnumTriviaStatesOfGame.end_game));
		yield* fork(loungeScreenFlow);
		return
	}

	/**
	 * loading part of the game
	 */
	yield* put(changeStateOfGame(EnumTriviaStatesOfGame.loading));

}

// checkGameStateSaga logic block end

/**
 * Disabled these rules because it ease to read if we have all code in the one place.
 * TODO: refactor and move this function to components
 */
// tslint:disable-next-line:cyclomatic-complexity max-func-body-length
export const checkGameStateSaga = function* (): SagaIterator {
	try {
		const lastCompletedContest = yield* select(getLastCompletedContest);
		const round = yield* select(getActualContestRound);

		yield* call(clearLiveQuestionsSaga);

		if (IS_TRIVIA_APP) {
			yield* fork(getUsersResult);
		}

		if (round) {
			yield* call(checkGameStateLogic, round, lastCompletedContest)
		} else {
			/**
			 * Set end game screens
			 */
			if (postGameState(lastCompletedContest)) {
				yield* put(changeStateOfGame(EnumTriviaStatesOfGame.end_game));
				yield* fork(loungeScreenFlow);
				return
			}
		}

		yield* fork(loungeScreenFlow);
		return
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))
	}
};
// eslint-enable complexity


export const loungeScreenFlow = function* (): SagaIterator {

	const contest = yield* select(getActualContest);
	const state_of_game = yield* select(getStateOfGame);
	const ready_before_game = includes([
		EnumTriviaStatesOfGame.loading,
		EnumTriviaStatesOfGame.end_game,
	], state_of_game);

	if (contest) {
		const date = new Date(contest.start_date_time).getTime();
		const now = new Date().getTime();
		const TIME_CORNER = TIMINGS.time_to_switch_on_before_screens;

		if (
			date > now &&
			date - Date.now() < TIME_CORNER &&
			ready_before_game
		) {
			yield* put(changeStateOfGame(EnumTriviaStatesOfGame.before_game));
		}
	}

	const is_need_to_check = !contest || ready_before_game;

	if (is_need_to_check) {
		yield* delay(5000);
		yield* call(loungeScreenFlow);
	}

};

interface IBEAnswerChoices {
	'id': number,
	'value': number,
	'is_correct': boolean
}

interface IBEAnswerVariant {
	'user_id': number,
	'question_id': number,
	'points': number,
	'position': number,
	'answer_choices': IBEAnswerChoices[]
}

export const getUsersResult = function* (): SagaIterator {
	if (!IS_TRIVIA_APP) {
		return
	}

	try {
		const round = yield* select(getLastGameRound);

		if (!round) {
			return null
		}

		const response = yield* call(TriviaApi.Trivia.answers, round.id);
		ApiError.CHECK(response);

		const {result} = response;

		const getAnswerState = (choices: IBEAnswerChoices[]) => {
			if ( some(choices, 'is_correct') ) {
				return EnumAnswerStates.correct
			}
			return EnumAnswerStates.incorrect
		};

		if ( result ) {
			const answers: IQuestionResultAction[] = toArray(result.answers).map((pick: IBEAnswerVariant) => {
				return {
					question_id: pick.question_id,
					points: pick.points,
					answer_state: getAnswerState(pick.answer_choices),
					round_id: round.id,
					position: pick.position
				}
			});

			yield* put(setQuestionsResults({
				questions: keyBy(answers, 'question_id'),
				wager: result.wager_value
			}))
		}

		yield* put(recordQuestions(prepareQuestionsToStore(round.questions, round.id)));

	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))

	}
};

export const setUserWager = function* (action: ISagaAction<ISetWagerAction>): SagaIterator {
	try {
		const contest_points = yield* select(getContestPointsFromWagersSelector);
		const {errors} = yield* call(TriviaApi.User.wager_round, action.payload);
		ApiError.CHECK({errors});

		yield* put(getWagerSuccess({
			contest_points,
			wager_value: action.payload.wager
		}));
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))
	}
};

export const getUserWager = function* (action: ISagaAction<ISetWagerAction['round_id']>): SagaIterator {
	try {
		const next_scheduled_round = yield* select(getScheduledRound);
		const id = action.payload || next_scheduled_round?.id;
		if (id) {
			const response = yield* call(TriviaApi.User.get_wager_of_round, action.payload);
			ApiError.CHECK(response);

			const {result} = response;
			yield* put(getWagerSuccess({
				wager_value: result.wager,
				contest_points: result.contest_points
			}));
		}
	} catch (e: any) {
		console.log(e);
		const error = e as Error;
		yield* put(globalError(error))
	}
};
